import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearReaderTextSelection,
  isChapterSwipeBlockedTarget,
  isHorizontalSwipeIntent,
} from '../../../utils/readerTextSelection';
/**
 * BibleFirst — кастомные хуки уровня UX-инфраструктуры.
 * Все хуки безопасны для SSR (проверяют `typeof window`).
 */

/* =============================================================
   FEATURE FLAG
   ============================================================= */

const BF_FLAG_KEY = 'bf-redesign';

/**
 * useBfFeatures — feature flag: новый UI включён по умолчанию.
 *  - ?bf=1 — включить и записать bf-redesign=1
 *  - ?bf=0 — выключить и записать bf-redesign=0
 *  - иначе: выключено только если в localStorage bf-redesign=0
 *
 * Возвращает { enabled, enable, disable, toggle }.
 */
export function useBfFeatures() {
  const initial = useMemo(() => {
    if (typeof window === 'undefined') return true;
    try {
      const params = new URLSearchParams(window.location.search || '');
      const fromUrl = params.get('bf');
      if (fromUrl === '1') {
        localStorage.setItem(BF_FLAG_KEY, '1');
        return true;
      }
      if (fromUrl === '0') {
        localStorage.setItem(BF_FLAG_KEY, '0');
        return false;
      }
      return localStorage.getItem(BF_FLAG_KEY) !== '0';
    } catch {
      return true;
    }
  }, []);

  const [enabled, setEnabled] = useState(initial);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (e) => {
      if (e.key === BF_FLAG_KEY) setEnabled(e.newValue !== '0');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const enable = useCallback(() => {
    try {
      localStorage.setItem(BF_FLAG_KEY, '1');
    } catch {
      /* no-op */
    }
    setEnabled(true);
  }, []);
  const disable = useCallback(() => {
    try {
      localStorage.setItem(BF_FLAG_KEY, '0');
    } catch {
      /* no-op */
    }
    setEnabled(false);
  }, []);
  const toggle = useCallback(() => (enabled ? disable() : enable()), [enabled, disable, enable]);

  return { enabled, enable, disable, toggle };
}

/* =============================================================
   READING SIZE — динамический размер текста чтения
   ============================================================= */

const READING_SIZE_KEY = 'bf-reading-size';
/* Premium mobile reading scale — центрирована на 20–22px (Apple Books / Kindle). */
export const BF_READING_SIZES = [17, 18.5, 20, 22, 24, 26];
const READING_SIZE_DEFAULT = 20;

export function useReadingSize() {
  const initial = useMemo(() => {
    if (typeof window === 'undefined') return READING_SIZE_DEFAULT;
    const raw = parseFloat(localStorage.getItem(READING_SIZE_KEY));
    return BF_READING_SIZES.includes(raw) ? raw : READING_SIZE_DEFAULT;
  }, []);
  const [size, setSize] = useState(initial);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty('--bf-reading-size', `${size}px`);
    try {
      localStorage.setItem(READING_SIZE_KEY, String(size));
    } catch {
      /* no-op */
    }
  }, [size]);

  return [size, setSize];
}

/* =============================================================
   THEME — light / dark / auto (system)
   ============================================================= */

const THEME_KEY = 'bf-theme';
export const BF_THEMES = ['dark', 'light'];

/**
 * useBfTheme — тема чтения: только тёмная или светлая (без «системной»).
 * По умолчанию — светлая. `data-bf-theme` выставляется синхронно в `main.jsx`.
 */
export function useBfTheme() {
  const initial = useMemo(() => {
    if (typeof window === 'undefined') return 'light';
    const v = localStorage.getItem(THEME_KEY);
    return v === 'dark' ? 'dark' : 'light';
  }, []);
  const [theme, setTheme] = useState(initial);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const resolved = theme === 'light' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-bf-theme', resolved);
    try {
      localStorage.setItem(THEME_KEY, resolved);
    } catch {
      /* no-op */
    }
  }, [theme]);

  return [theme, setTheme];
}

/** Переключение dark ↔ light (кнопка в шапке). */
export function bfNextTheme(current) {
  return current === 'dark' ? 'light' : 'dark';
}

/* =============================================================
   AMBIENT MODE — distraction-free reading
   ============================================================= */

/**
 * useAmbientMode — управляет ambient-режимом чтения:
 *  - двойной тап по пустому месту в области чтения → toggle
 *  - в ambient-режиме скрываются header / progress / floating
 *
 * Возвращает { ambient, toggle, setAmbient, getContainerProps }.
 * getContainerProps подключай на корневой ридер-контейнер.
 */
export function useAmbientMode() {
  const [ambient, setAmbient] = useState(false);
  const lastTap = useRef(0);

  const toggle = useCallback(() => setAmbient((v) => !v), []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (ambient) document.documentElement.setAttribute('data-bf-ambient', 'true');
    else document.documentElement.removeAttribute('data-bf-ambient');
    return () => document.documentElement.removeAttribute('data-bf-ambient');
  }, [ambient]);

  const getContainerProps = useCallback(
    () => ({
      onDoubleClick: (e) => {
        // не реагируем на двойной тап по самим стихам/кнопкам
        const target = e.target;
        if (target?.closest?.('.bf-verse, button, a, input, [role="button"], .bf-popover')) {
          return;
        }
        const now = Date.now();
        if (now - lastTap.current < 60) return;
        lastTap.current = now;
        toggle();
      },
    }),
    [toggle]
  );

  return { ambient, setAmbient, toggle, getContainerProps };
}

/* =============================================================
   CHAPTER SWIPE — навигация между главами
   ============================================================= */

function isSwipeBlockedTarget(target) {
  return isChapterSwipeBlockedTarget(target);
}

/** Активен горизонтальный жест «как в Telegram» — стихи не должны ловить тап/лонгтап */
let chapterSwipeGestureActive = false;

export function isChapterSwipeGestureActive() {
  return chapterSwipeGestureActive;
}

export function setChapterSwipeGestureActive(active, rootEl) {
  chapterSwipeGestureActive = active;
  if (typeof document !== 'undefined') {
    if (active) document.documentElement.setAttribute('data-bf-chapter-swipe', 'true');
    else document.documentElement.removeAttribute('data-bf-chapter-swipe');
  }
  if (rootEl) {
    rootEl.classList.toggle('bf-chapter-swiping', active);
  }
}

const CHAPTER_SWIPE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const CHAPTER_SWIPE_SNAP_MS = 220;
const CHAPTER_SWIPE_COMMIT_MS = 200;

function getSwipeLayers(el) {
  const viewport = el.querySelector('.bf-chapter-swipe-viewport');
  if (!viewport) {
    return { mode: 'legacy', current: el.querySelector('.verses-container') };
  }
  const track = viewport.querySelector('.bf-chapter-swipe-track');
  return {
    mode: 'stack',
    viewport,
    track,
    current: viewport.querySelector('.bf-chapter-swipe-panel--current'),
    prev: viewport.querySelector('.bf-chapter-swipe-panel--prev'),
    next: viewport.querySelector('.bf-chapter-swipe-panel--next'),
  };
}

function getStackMetrics(el, layers) {
  const w = Math.round(el.clientWidth || 0);
  return {
    w,
    baseOffset: 0,
    hasPrev: !!layers.prev,
    hasNext: !!layers.next,
  };
}

export function getReadingClipTop() {
  if (typeof document === 'undefined') return 0;
  const header = document.querySelector('.bf-header');
  if (header) return Math.round(header.getBoundingClientRect().bottom);
  return 0;
}

/** Сдвиг превью соседней главы: стих 1 у верхней кромки чтения (как Telegram). */
export function computeChapterSwipeNeighborY(viewportEl) {
  if (!viewportEl) return 0;
  const readingTop = getReadingClipTop();
  const vTop = viewportEl.getBoundingClientRect().top;
  return Math.round(readingTop - vTop);
}

function computeNeighborYOffset(layers) {
  if (!layers.viewport) return 0;
  const vTop = layers.viewport.getBoundingClientRect().top;
  const readingTop = getReadingClipTop();
  if (vTop < 0) return Math.round(-vTop);
  return Math.round(Math.max(0, readingTop - vTop));
}

function applyNeighborPanelOffset(layers, yPx) {
  if (!layers) return;
  const y = `${yPx}px`;
  if (layers.prev) layers.prev.style.transform = `translate3d(-100%, ${y}, 0)`;
  if (layers.next) layers.next.style.transform = `translate3d(100%, ${y}, 0)`;
}

function clearNeighborPanelOffset(layers) {
  if (!layers) return;
  if (layers.prev) layers.prev.style.transform = 'translate3d(-100%, 0, 0)';
  if (layers.next) layers.next.style.transform = 'translate3d(100%, 0, 0)';
}

function rubberBandOffset(delta, limit) {
  const ad = Math.abs(delta);
  if (ad <= limit) return delta;
  const sign = delta < 0 ? -1 : 1;
  return sign * (limit + (ad - limit) * 0.22);
}

function resetSwipeStyles(el, layers) {
  if (!el) return;
  clearNeighborPanelOffset(layers);
  el.style.removeProperty('--bf-swipe-neighbor-y');
  el.classList.remove('bf-chapter-swipe-dragging');
  if (layers.mode === 'stack' && layers.track) {
    layers.track.style.transition = '';
    layers.track.style.transform = 'translate3d(0, 0, 0)';
    layers.track.classList.remove('bf-chapter-swipe-active');
    return;
  }
  if (layers.current) {
    layers.current.style.transition = '';
    layers.current.style.transform = '';
    layers.current.style.opacity = '';
    layers.current.classList.remove('bf-chapter-swipe-active');
  }
}

function isTrackStuckOffCenter(track) {
  if (!track) return false;
  const t = track.style.transform || '';
  if (!t || t === 'none') return false;
  return !/translate3d\(\s*0(?:px)?\s*,\s*0(?:px)?\s*,\s*0(?:px)?\s*\)/.test(t);
}

/** Сброс DOM свайпа после навигации или залипшего transform. */
export function resetChapterSwipeContainer(rootEl) {
  if (!rootEl) return;
  const layers = getSwipeLayers(rootEl);
  resetSwipeStyles(rootEl, layers);
  setChapterSwipeGestureActive(false, rootEl);
}

/**
 * Свайп по области чтения (Telegram): трек из 3 панелей — пред / текущая / след. глава.
 * Без pointer capture — тап по стиху и вертикальный скролл сохраняются.
 */
export function useChapterSwipe(
  targetRef,
  {
    enabled = true,
    onPrev,
    onNext,
    onSwipeEngaged,
    minDx = 44,
    maxDy = 100,
    lockDx = 16,
    remountKey = 0,
  } = {}
) {
  const onPrevRef = useRef(onPrev);
  const onNextRef = useRef(onNext);
  const onSwipeEngagedRef = useRef(onSwipeEngaged);
  onPrevRef.current = onPrev;
  onNextRef.current = onNext;
  onSwipeEngagedRef.current = onSwipeEngaged;

  useEffect(() => {
    const el = targetRef?.current;
    if (!el || !enabled) {
      setChapterSwipeGestureActive(false, null);
      return undefined;
    }

    let startX = null;
    let startY = null;
    let horizontalLock = false;
    let activePointerId = null;
    let windowBound = false;
    let swipeMetrics = null;
    let frozenNeighborY = 0;
    let snapTimerId = null;

    const clearSnapTimer = () => {
      if (snapTimerId != null) {
        window.clearTimeout(snapTimerId);
        snapTimerId = null;
      }
    };

    const snapContentBack = () => {
      clearSnapTimer();
      const layers = getSwipeLayers(el);
      const transition = `transform ${CHAPTER_SWIPE_SNAP_MS}ms ${CHAPTER_SWIPE_EASE}`;

      if (layers.mode === 'stack' && layers.track) {
        layers.track.classList.remove('bf-chapter-swipe-active');
        layers.track.style.transition = transition;
        layers.track.style.transform = 'translate3d(0, 0, 0)';
        snapTimerId = window.setTimeout(() => {
          snapTimerId = null;
          if (!layers.track) return;
          layers.track.style.transition = '';
          clearNeighborPanelOffset(layers);
          el.style.removeProperty('--bf-swipe-neighbor-y');
          frozenNeighborY = 0;
          el.classList.remove('bf-chapter-swipe-dragging');
        }, CHAPTER_SWIPE_SNAP_MS + 30);
        return;
      }

      const content = layers.current;
      if (!content) {
        el.classList.remove('bf-chapter-swipe-dragging');
        return;
      }
      content.classList.remove('bf-chapter-swipe-active');
      content.style.transition = transition;
      content.style.transform = '';
      content.style.opacity = '';
      snapTimerId = window.setTimeout(() => {
        snapTimerId = null;
        resetSwipeStyles(el, layers);
        el.classList.remove('bf-chapter-swipe-dragging');
      }, CHAPTER_SWIPE_SNAP_MS + 30);
    };

    const applyDragOffset = (dx) => {
      const layers = getSwipeLayers(el);
      const resist = 0.34;

      if (layers.mode === 'stack' && layers.track) {
        const metrics = swipeMetrics || getStackMetrics(el, layers);
        if (metrics.w <= 0) return;
        swipeMetrics = metrics;
        layers.track.style.setProperty('--bf-swipe-panel-px', `${metrics.w}px`);

        let x = dx;
        const canRevealNext = metrics.hasNext && dx < 0;
        const canRevealPrev = metrics.hasPrev && dx > 0;

        if (dx < 0 && !canRevealNext) {
          x = onNextRef.current ? rubberBandOffset(dx, metrics.w * 0.38) : dx * resist;
        } else if (dx > 0 && !canRevealPrev) {
          x = onPrevRef.current ? rubberBandOffset(dx, metrics.w * 0.38) : dx * resist;
        }

        applyNeighborPanelOffset(layers, frozenNeighborY);
        layers.track.classList.add('bf-chapter-swipe-active');
        layers.track.style.transition = 'none';
        layers.track.style.transform = `translate3d(${x}px, 0, 0)`;
        return;
      }

      const content = layers.current;
      if (!content) return;
      const w = el.clientWidth || window.innerWidth || 360;
      let x = dx;
      if (dx < 0 && !onNextRef.current) x = dx * resist;
      if (dx > 0 && !onPrevRef.current) x = dx * resist;
      x = rubberBandOffset(x, w * 0.42);
      content.classList.add('bf-chapter-swipe-active');
      content.style.transition = 'none';
      content.style.transform = `translate3d(${x}px, 0, 0)`;
    };

    const commitSlide = (dir, navigateFn) => {
      const layers = getSwipeLayers(el);
      const transition = `transform ${CHAPTER_SWIPE_COMMIT_MS}ms ${CHAPTER_SWIPE_EASE}`;

      if (layers.mode === 'stack' && layers.track) {
        const metrics = swipeMetrics || getStackMetrics(el, layers);
        const w = metrics.w || Math.round(el.clientWidth || window.innerWidth || 360);
        const targetOffset = dir === 'next' ? -w : w;

        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          clearSnapTimer();
          layers.track?.removeEventListener('transitionend', onEnd);
          swipeMetrics = null;
          frozenNeighborY = 0;
          el.classList.remove('bf-chapter-swipe-dragging');
          setChapterSwipeGestureActive(false, el);
          /* Трек остаётся на targetOffset — ChapterView сбросит в useLayoutEffect после смены главы */
          navigateFn();
        };

        const onEnd = (ev) => {
          if (ev.target !== layers.track || ev.propertyName !== 'transform') return;
          finish();
        };

        layers.track.style.transition = transition;
        layers.track.style.transform = `translate3d(${targetOffset}px, 0, 0)`;
        layers.track.addEventListener('transitionend', onEnd);
        window.setTimeout(finish, CHAPTER_SWIPE_COMMIT_MS + 100);
        return;
      }

      const content = layers.current;
      const w = el.clientWidth || window.innerWidth || 360;
      const targetX = dir === 'next' ? -w : w;
      if (!content) {
        navigateFn();
        return;
      }
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resetSwipeStyles(el, layers);
        navigateFn();
      };
      content.style.transition = transition;
      content.style.transform = `translate3d(${targetX}px, 0, 0)`;
      const onEnd = (ev) => {
        if (ev.target !== content || ev.propertyName !== 'transform') return;
        content.removeEventListener('transitionend', onEnd);
        finish();
      };
      content.addEventListener('transitionend', onEnd);
      window.setTimeout(finish, CHAPTER_SWIPE_COMMIT_MS + 80);
    };

    const endTracking = (keepDraggingClass = false) => {
      startX = null;
      startY = null;
      horizontalLock = false;
      activePointerId = null;
      setChapterSwipeGestureActive(false, el);
      if (!keepDraggingClass) {
        const layers = getSwipeLayers(el);
        clearNeighborPanelOffset(layers);
        el.style.removeProperty('--bf-swipe-neighbor-y');
        frozenNeighborY = 0;
        el.classList.remove('bf-chapter-swipe-dragging');
      }
    };

    const unbindWindow = () => {
      if (!windowBound) return;
      windowBound = false;
      window.removeEventListener('pointermove', onPointerMove, moveOpts);
      window.removeEventListener('pointerup', onPointerUp, upOpts);
      window.removeEventListener('pointercancel', onPointerCancel, upOpts);
    };

    const bindWindow = () => {
      if (windowBound) return;
      windowBound = true;
      window.addEventListener('pointermove', onPointerMove, moveOpts);
      window.addEventListener('pointerup', onPointerUp, upOpts);
      window.addEventListener('pointercancel', onPointerCancel, upOpts);
    };

    const tryLockHorizontal = (clientX, clientY) => {
      if (horizontalLock || startX == null || startY == null) return false;
      const dx = clientX - startX;
      const dy = clientY - startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      if (adx < lockDx) return false;
      if (ady > adx * 0.72) return false;

      clearReaderTextSelection();
      horizontalLock = true;
      setChapterSwipeGestureActive(true, el);
      el.classList.add('bf-chapter-swipe-dragging');
      onSwipeEngagedRef.current?.();

      const layers = getSwipeLayers(el);
      if (layers.mode === 'stack' && layers.track) {
        swipeMetrics = getStackMetrics(el, layers);
        layers.track.style.setProperty('--bf-swipe-panel-px', `${swipeMetrics.w}px`);
        /* Без вертикального сдвига соседних панелей — иначе при захвате жеста «дёргает» вверх/вниз */
        frozenNeighborY = 0;
        clearNeighborPanelOffset(layers);
      }
      applyDragOffset(0);
      return true;
    };

    const onPointerMove = (e) => {
      if (activePointerId == null || e.pointerId !== activePointerId) return;
      if (startX == null) return;

      if (!horizontalLock) {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);
        /* Вертикальный скролл — отпускаем жест, не боремся с нативной прокруткой */
        if (ady >= 14 && ady > adx * 1.15) {
          unbindWindow();
          endTracking(false);
          return;
        }
        if (!isHorizontalSwipeIntent(dx, dy, 14)) return;
        if (!tryLockHorizontal(e.clientX, e.clientY)) return;
      }

      clearReaderTextSelection();
      e.preventDefault();
      applyDragOffset(e.clientX - startX);
    };

    const onPointerUp = (e) => {
      if (activePointerId == null || e.pointerId !== activePointerId) return;
      unbindWindow();

      const sx = startX;
      const sy = startY;
      const wasLock = horizontalLock;
      const dx = sx != null ? e.clientX - sx : 0;
      const dy = sy != null ? e.clientY - sy : 0;

      if (!wasLock || sx == null) {
        endTracking(false);
        swipeMetrics = null;
        snapContentBack();
        return;
      }

      endTracking(true);

      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      const commit =
        adx >= minDx &&
        !(ady > maxDy && ady > adx) &&
        adx > ady * 1.2;

      if (commit && dx < 0 && onNextRef.current) {
        bfHaptic('light');
        commitSlide('next', onNextRef.current);
        return;
      }
      if (commit && dx > 0 && onPrevRef.current) {
        bfHaptic('light');
        commitSlide('prev', onPrevRef.current);
        return;
      }
      snapContentBack();
    };

    const onPointerCancel = (e) => {
      if (activePointerId == null || e.pointerId !== activePointerId) return;
      unbindWindow();
      endTracking(false);
      swipeMetrics = null;
      snapContentBack();
    };

    const onPointerDown = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (activePointerId != null) return;
      if (isSwipeBlockedTarget(e.target)) return;

      clearSnapTimer();
      clearReaderTextSelection();

      const layers = getSwipeLayers(el);
      if (layers.track && isTrackStuckOffCenter(layers.track)) {
        resetSwipeStyles(el, layers);
      }

      activePointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      horizontalLock = false;
      swipeMetrics = null;
      bindWindow();
    };

    const captureOpts = { capture: true };
    const moveOpts = { capture: true, passive: false };
    const upOpts = { capture: true };

    el.addEventListener('pointerdown', onPointerDown, captureOpts);

    return () => {
      unbindWindow();
      el.removeEventListener('pointerdown', onPointerDown, captureOpts);
      clearSnapTimer();
      resetChapterSwipeContainer(el);
    };
  }, [targetRef, enabled, minDx, maxDy, lockDx, remountKey]);
}

/**
 * useEdgeSwipeChapter — слушает touch-жесты по левому/правому краю экрана.
 * При swipe ≥ 60px горизонтально и < 40px вертикально вызывает соответствующий callback.
 * Работает с touch-событиями (mobile-first), не мешает обычному скроллу.
 */
export function useEdgeSwipeChapter({ onPrev, onNext, edgeWidth = 32, minDx = 60, maxDy = 40 } = {}) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let startX = null;
    let startY = null;
    let fromLeftEdge = false;
    let fromRightEdge = false;

    const onStart = (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      startX = touch.clientX;
      startY = touch.clientY;
      const w = window.innerWidth || document.documentElement.clientWidth || 0;
      fromLeftEdge = startX <= edgeWidth;
      fromRightEdge = w - startX <= edgeWidth;
    };
    const onEnd = (e) => {
      if (startX == null) return;
      const touch = e.changedTouches?.[0];
      if (!touch) {
        startX = null;
        return;
      }
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      startX = null;
      startY = null;
      if (Math.abs(dy) > maxDy) return;
      if (fromLeftEdge && dx > minDx) onPrev?.();
      else if (fromRightEdge && dx < -minDx) onNext?.();
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchend', onEnd);
    };
  }, [onPrev, onNext, edgeWidth, minDx, maxDy]);
}

/* =============================================================
   SAFE HAPTIC HELPERS
   ============================================================= */

export function bfHaptic(kind = 'light') {
  try {
    if (typeof window !== 'undefined' && window?.Telegram?.WebApp?.HapticFeedback) {
      const h = window.Telegram.WebApp.HapticFeedback;
      if (kind === 'success' || kind === 'error' || kind === 'warning') h.notificationOccurred(kind);
      else h.impactOccurred(kind);
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(kind === 'rigid' || kind === 'heavy' ? 14 : 6);
    }
  } catch {
    /* no-op */
  }
}
